import React from 'react';

/**
 * Convert raw error messages to user-friendly messages.
 *
 * Phase 11 / Task 19: a locked-version 423 response carries
 *   { error: '...', code: 'version_locked' }
 * Callers that pass the parsed JSON body (or the original Error preserving
 * .code) get a tailored toast; string callers still get the generic path.
 */
// 409 `duplicate_in_flight` from a protected create (middleware/idempotency.js
// server-side): an EARLIER attempt with the same Idempotency-Key is still
// being processed — or was stranded by a server restart, in which case the
// key self-releases after the middleware's staleness cutoff (~15 min).
// The copy must tell the user what to DO — check whether the thing was
// created before pressing again — because the client correctly REUSES the
// key on retry (hooks/useIdempotencyKey.js), so every further press repeats
// this answer until the first attempt resolves or the cutoff passes. The raw
// server string is an implementation detail and must not be what they see.
const DUPLICATE_IN_FLIGHT_MESSAGE =
  'An earlier attempt to submit this may still be processing. ' +
  'Wait a moment and check whether it was created before trying again.';

// Orders-are-all-hire-or-non-hire (owner ruling, 2026-09-02) — both refusal
// codes carry prose that is ALREADY user-facing (written for exactly this
// purpose at the server, `server/routes/orders.js`'s `HireModeConflictRefusal`
// / `DuplicateSourceMixedHireRefusal`), so these two entries pass the message
// straight through rather than writing a second copy of the same sentence
// that could drift from the first. Added FIX ROUND 1 (adversarial review,
// finding 1): before this, `{ error: 'code: prose', code: 'code' }`'s prose
// half carried the code jammed into it and reached users VERBATIM — this
// repo's own recorded incident of the same shape, hours earlier, was a raw
// `adjustments_exist` token in an `alert()` in the extend dialog.
//
// 🔑 `apiThrow` (`utils/api.js`) builds `new Error(data.error)` and copies
// `data.code` onto `err.code`, so `error.message` — never `error.error`,
// which does not exist on this shape — carries the prose. Now that the
// server splits code from prose, this branch is technically redundant with
// the generic `error?.message` fallback below (both resolve to the same
// clean string); it stays explicit anyway, matching the `version_locked` /
// `duplicate_in_flight` shortcuts immediately above, so a caller that only
// checks `.code` here — or a future refactor of the fallback — cannot
// silently regress this specific pair.
//
// 🕶️ HONEST MUTATION NOTE: disabling this whole branch (`if (false)`)
// leaves every test in the file below GREEN — the generic fallback already
// returns the identical clean string, because the real fix is upstream
// (the server no longer jams the code into the prose). This branch cannot
// be proven load-bearing by mutation today; it is deliberate redundancy,
// not a gap. Recorded rather than silently claimed as bite-proven.
const ORDER_HIRE_MODE_CODES = new Set(['order_hire_mode_conflict', 'duplicate_source_mixed_hire']);

// 409 `order_changed` (2026-10-28, `server/helpers/rowLock.js`): someone moved
// the order's status — ticked its last line ordered, say — between this screen
// loading it and the save, so the server refused rather than write over it.
// Nothing was saved; the form keeps what was typed. Exported so a caller's
// test can assert THIS sentence rather than re-typing it.
export const ORDER_CHANGED_MESSAGE =
  'This order changed while you were editing it — reload to see the latest. '
  + 'Nothing was saved; your changes are still in the form.';

export function friendlyErrorMessage(error) {
  if (error && typeof error === 'object' && error.code === 'order_changed') {
    return ORDER_CHANGED_MESSAGE;
  }
  // Locked-version shortcut — check the structured code BEFORE coercing to msg.
  if (error && typeof error === 'object' && error.code === 'version_locked') {
    return 'This budget version is locked. Unlock or clone it to make changes.';
  }
  // Duplicate-in-flight shortcut — same shape as version_locked above.
  if (error && typeof error === 'object' && error.code === 'duplicate_in_flight') {
    return DUPLICATE_IN_FLIGHT_MESSAGE;
  }
  if (error && typeof error === 'object' && ORDER_HIRE_MODE_CODES.has(error.code) && typeof error.message === 'string') {
    return error.message;
  }

  const msg = typeof error === 'string' ? error : (error?.message || 'Unknown error');

  // Duplicate-in-flight (text-only) — most call sites `throw new Error(data.error)`,
  // which drops `.code`, so the server's error string is the signal that
  // actually arrives here. Match the exact phrase the 409 body carries.
  if (msg.includes('Idempotency-Key is already being processed') || msg.includes('duplicate_in_flight')) {
    return DUPLICATE_IN_FLIGHT_MESSAGE;
  }

  // Locked-version (text-only) — server message also stuffs the same wording
  // into the `error` field, so a generic Error(throw new Error(data.error))
  // path lands here on the substring match.
  if (msg.includes('budget version is locked') || msg.includes('version_locked')) {
    return 'This budget version is locked. Unlock or clone it to make changes.';
  }

  // Network/connection errors
  if (msg === 'Failed to fetch' || msg.includes('NetworkError') || msg.includes('net::ERR_')) {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }
  if (msg.includes('ERR_CONNECTION_REFUSED') || msg.includes('ECONNREFUSED')) {
    return 'The server is not responding. Please try again in a moment.';
  }
  if (msg.includes('timeout') || msg.includes('Timeout') || msg.includes('ETIMEDOUT')) {
    return 'The request timed out. Please check your connection and try again.';
  }
  if (msg.includes('503') || msg.includes('Service Unavailable')) {
    return 'The server is temporarily unavailable. Please try again shortly.';
  }
  if (msg.includes('500') || msg.includes('Internal Server Error')) {
    return 'Something went wrong on the server. Please try again.';
  }

  return msg;
}

/**
 * Reusable error message component with optional retry button
 */
function ErrorMessage({ error, onRetry, title }) {
  const message = friendlyErrorMessage(error);

  return (
    <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <svg className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <div className="flex-1">
          {title && <p className="font-semibold text-red-800 dark:text-red-300 mb-1">{title}</p>}
          <p className="text-red-700 dark:text-red-300 text-sm">{message}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-900/60 text-red-800 dark:text-red-300 text-sm font-medium rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ErrorMessage;
